import { useState } from 'react';
import { ChevronDown, Server } from 'lucide-react';
import { ServerConfig } from '../types/player';
import { cn } from '../utils/cn';
import { useIsMobile } from '../hooks/useIsMobile';
import { PageHeader } from './ui/PageHeader';

interface ClusterSelectorProps {
  title: string;
  description: string;
  configs: ServerConfig[];
  selectedCluster: string;
  onSelect: (cluster: string) => void;
  /** Optional nav-category kicker rendered above the title (e.g. "Economy"). */
  eyebrow?: string;
}

export const ClusterSelector = ({ title, description, configs, selectedCluster, onSelect, eyebrow }: ClusterSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();

  const uniqueClusters = configs.reduce((acc, current) => {
    if (current.cluster_name && !acc.find((item) => item.cluster_name === current.cluster_name)) {
      acc.push(current);
    }
    return acc;
  }, [] as ServerConfig[]).sort((a, b) => a.cluster_name.localeCompare(b.cluster_name));

  const selectedConfig = uniqueClusters.find((c) => c.cluster_name === selectedCluster);

  return (
    <PageHeader
      eyebrow={eyebrow}
      title={title}
      description={description}
      actions={
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          className="glass-button touch-manipulation max-w-full"
        >
          {selectedConfig?.thumbnail_url ? (
            <img src={selectedConfig.thumbnail_url} alt="" className="w-5 h-5 rounded object-cover flex-shrink-0" />
          ) : (
            <Server className="w-4 h-4 text-glass-400 flex-shrink-0" />
          )}
          <span className="hidden sm:inline text-glass-400 font-normal">Cluster</span>
          <span className="font-semibold text-accent-300 truncate max-w-[120px] sm:max-w-[220px]">{selectedCluster || 'Select…'}</span>
          <ChevronDown className={cn("w-4 h-4 text-glass-400 transition-transform flex-shrink-0", isOpen && "rotate-180")} />
        </button>
      }
    >
      {isOpen && (
        <div className="glass-card p-3 md:p-4">
          <div className="flex items-center justify-between gap-3 mb-3">
            <span className="eyebrow">Select a cluster</span>
            <span className="text-xs text-glass-500 tabular-nums">{uniqueClusters.length} available</span>
          </div>
          {isMobile ? (
            /* Mobile: Horizontal scrollable strip of cluster thumbnails */
            <div className="flex overflow-x-auto gap-2 pb-1 scrollbar-hide -mx-1 px-1">
              {uniqueClusters.map(config => (
                <button
                  key={config.cluster_name}
                  onClick={() => { onSelect(config.cluster_name); setIsOpen(false); }}
                  aria-pressed={selectedCluster === config.cluster_name}
                  className={cn(
                    "flex-shrink-0 p-2 rounded-lg border transition-colors touch-manipulation",
                    selectedCluster === config.cluster_name
                      ? 'bg-accent-500/15 border-accent-400/60'
                      : 'bg-glass-100/5 border-glass-200/10 active:border-accent-400/50'
                  )}
                  title={config.cluster_name}
                >
                  <img src={config.thumbnail_url} alt={config.cluster_name} className="w-12 h-12 object-cover rounded-md" />
                  <p className="text-center text-[10px] mt-1 text-white truncate w-12">{config.cluster_name}</p>
                </button>
              ))}
            </div>
          ) : (
            /* Desktop: Grid of cluster thumbnails */
            <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
              {uniqueClusters.map(config => (
                <button
                  key={config.cluster_name}
                  onClick={() => { onSelect(config.cluster_name); setIsOpen(false); }}
                  aria-pressed={selectedCluster === config.cluster_name}
                  className={cn(
                    "p-2 rounded-lg border transition-colors",
                    selectedCluster === config.cluster_name
                      ? 'bg-accent-500/15 border-accent-400/60'
                      : 'bg-glass-100/5 border-glass-200/10 hover:border-glass-200/25 hover:bg-glass-100/10'
                  )}
                  title={config.cluster_name}
                >
                  <img src={config.thumbnail_url} alt={config.cluster_name} className="w-16 h-16 object-cover rounded-md mx-auto" />
                  <p className="text-center text-xs mt-2 text-white truncate">{config.cluster_name}</p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </PageHeader>
  );
};
